import * as React from "react";
import { useRef, useState, useMemo, useEffect, useCallback } from "react";
import {
  Box,
  Paper,
  Popper,
  Typography,
  InputAdornment,
  ClickAwayListener,
} from "@mui/material";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Close, Store } from "@mui/icons-material";
import {
  StyledCheckbox,
  StyledMenuItemContainer,
  StyledMenuListItemText,
} from "../StyledComponents/styled";
import { CustomTextInput } from "../TextInputs/style";
import { erase_duplicates } from "../../../api_calls/utils";

const MAX_LABEL_CHARS = 50;
const CHAR_WIDTH_PX = 6.5;
const DROPDOWN_PADDING_PX = 80;
const BOTTOM_MARGIN_PX = 100;
const TOP_MARGIN_PX = 8;
const MIN_DROPDOWN_HEIGHT = 150;
const FLIP_THRESHOLD_TO_TOP = 200;
const FLIP_THRESHOLD_TO_BOTTOM = 250;

const DROPDOWN_OPENED_EVENT = "dropdown-singleton-opened";

export default function MultiSelect({
  label,
  defaultVal,
  elemId,
  options,
  onChangeCall,
  fieldError,
  enableFullWidth,
  selectionCheck,
  usedFor,
  onBlurCall,
  showSelectedValues,
  readOnlyFlag,
  disableSearch,
  canNotClear,
  additionalIconWithTooltip,
  additionalIconMarkup,
  onClearCall,
  allowDuplicates
}) {
  const anchorRef = useRef(null);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);
  const optionRefs = useRef({});
  const popperRef = useRef(null);

  const [selectedValues, setSelectedValues] = useState(defaultVal);
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const [dropdownMaxHeight, setDropdownMaxHeight] = useState(MIN_DROPDOWN_HEIGHT);
  const [anchorIsFixed, setAnchorIsFixed] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  // Track placement both as state (for render) and as ref (for stable reads in callbacks)
  const placementRef = useRef("bottom-start");
  const [actualPlacement, setActualPlacement] = useState("bottom-start");

  const isDisabled = typeof readOnlyFlag !== "undefined" ? readOnlyFlag : false;
  const showSearch = typeof disableSearch !== "undefined" ? !disableSearch : true;
  const isFilter = usedFor === "filter";
  const shrink = isFocused || open || (selectedValues?.length ?? 0) > 0 || isFilter;

  const IP_REGEX =
    /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})(?:\/(\d{1,2}))?$/;

  const instanceId = useRef(
    elemId || `multi-select-${Math.random().toString(36).substr(2, 9)}`
  );

  const updatePlacement = useCallback((p) => {
    placementRef.current = p;
    setActualPlacement(p);
  }, []);

  // Determine placement using hysteresis
  const computePlacement = useCallback(() => {
    if (!anchorRef.current) return placementRef.current;

    const { bottom } = anchorRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - bottom;
    const current = placementRef.current;
    const isTop = current.startsWith("top");

    if (!isTop && spaceBelow < FLIP_THRESHOLD_TO_TOP) return "top-start";
    if (isTop && spaceBelow > FLIP_THRESHOLD_TO_BOTTOM) return "bottom-start";
    return current;
  }, []);

  // Compute max dropdown height based on resolved placement
  const computeHeight = useCallback((placement) => {
    if (!anchorRef.current) return MIN_DROPDOWN_HEIGHT;

    const { top, bottom } = anchorRef.current.getBoundingClientRect();
    const openingUpward = placement.startsWith("top");
    const available = openingUpward
      ? top - TOP_MARGIN_PX
      : window.innerHeight - bottom - BOTTOM_MARGIN_PX;

    return Math.max(available, MIN_DROPDOWN_HEIGHT);
  }, []);

  // Resolve placement first, then derive height from that placement
  const recalculate = useCallback(() => {
    const placement = computePlacement();
    updatePlacement(placement);
    setDropdownMaxHeight(computeHeight(placement));
  }, [computePlacement, computeHeight, updatePlacement]);

  const detectAnchorIsFixed = useCallback(() => {
    let el = anchorRef.current?.parentElement;

    // Pre-scan the full ancestor chain for a Dialog before evaluating overflow;
    // DialogContent (overflow: auto) sits below the Dialog root in the DOM, so a
    // simple top-down traversal would return early at the overflow node and never
    // reach role="dialog"
    let insideDialog = false;
    let scanEl = el;
    while (scanEl && scanEl !== document.body) {
      if (scanEl.getAttribute("role") === "dialog") { insideDialog = true; break; }
      scanEl = scanEl.parentElement;
    }

    while (el && el !== document.body) {
      const style = window.getComputedStyle(el);
      const position = style.position;
      const overflow = style.overflow + style.overflowY + style.overflowX;

      if (/auto|scroll|overlay/.test(overflow)) {
        // Anchor scrolls inside this container; fixed strategy is required when
        // that container belongs to a Dialog so the Popper can track the anchor
        return insideDialog;
      }

      if (position === "sticky" || position === "fixed") return true;

      el = el.parentElement;
    }

    return insideDialog;
  }, []);

  const close = useCallback(
    (shouldRefocus = false) => {
      setOpen(false);
      setSearchTerm("");
      setHighlightedIndex(-1);
      if (shouldRefocus) anchorRef.current?.focus();
    },
    []
  );

  const onBlurCallRef = useRef(onBlurCall);
  onBlurCallRef.current = onBlurCall;
  const wasOpenRef = useRef(false);

  useEffect(() => {
    if (wasOpenRef.current && !open) {
      onBlurCallRef.current?.();
    }
    wasOpenRef.current = open;
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const isInsideWidget = (node) =>
      !!(node && (anchorRef.current?.contains(node) || dropdownRef.current?.contains(node)));

    const handleFocusIn = (e) => {
      if (isInsideWidget(e.target)) return;
      close(false);
    };

    // Tab does not fire ClickAwayListener. Close (and therefore commit) on Tab
    // before focus leaves, including when a child stops keydown propagation.
    const handleTab = (e) => {
      if (e.key !== "Tab") return;
      if (!isInsideWidget(document.activeElement)) return;
      close(false);
    };

    document.addEventListener("focusin", handleFocusIn);
    document.addEventListener("keydown", handleTab, true);
    return () => {
      document.removeEventListener("focusin", handleFocusIn);
      document.removeEventListener("keydown", handleTab, true);
    };
  }, [open, close]);

  useEffect(() => {
    const handleOtherDropdownOpened = (e) => {
      if (e.detail?.id !== instanceId.current && open) close();
    };

    window.addEventListener(DROPDOWN_OPENED_EVENT, handleOtherDropdownOpened);
    return () => {
      window.removeEventListener(DROPDOWN_OPENED_EVENT, handleOtherDropdownOpened);
    };
  }, [open, close]);

  useEffect(() => {
    if (open) {
      const event = new CustomEvent(DROPDOWN_OPENED_EVENT, {
        detail: { id: instanceId.current },
      });
      window.dispatchEvent(event);
    }
  }, [open]);

  // On open: resolve placement first, then compute height; focus search input or
  // the Paper itself so keyboard navigation works even without a search field
  useEffect(() => {
    if (!open) return;

    setAnchorIsFixed(detectAnchorIsFixed());
    recalculate();

    if (showSearch) {
      setTimeout(() => searchInputRef.current?.focus(), 0);
    } else {
      setTimeout(() => dropdownRef.current?.focus(), 0);
    }
  }, [open, detectAnchorIsFixed, recalculate, showSearch]);

  // On viewport resize or zoom: recalculate placement and height while open
  useEffect(() => {
    if (!open) return;

    const handleResize = () => recalculate();
    const vp = window.visualViewport;

    if (vp) {
      vp.addEventListener("resize", handleResize);
      return () => vp.removeEventListener("resize", handleResize);
    }

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [open, recalculate]);

  // Close when anchor exits the viewport in the direction the dropdown is open;
  // for fixed-strategy anchors also force a synchronous position update on scroll
  useEffect(() => {
    if (!open) return;

    const handleScroll = () => {
      if (anchorRef.current) {
        const { top, bottom } = anchorRef.current.getBoundingClientRect();
        const openingUpward = placementRef.current.startsWith("top");
        if (openingUpward && top > window.innerHeight) { close(false); return; }
        if (!openingUpward && bottom < 0) { close(false); return; }
      }
      // Absolute-strategy poppers follow document scroll natively; only fixed ones need JS nudging
      if (anchorIsFixed) popperRef.current?.forceUpdate();
    };

    window.addEventListener("scroll", handleScroll, { capture: true, passive: true });
    return () => window.removeEventListener("scroll", handleScroll, { capture: true });
  }, [open, anchorIsFixed, close]);

  useEffect(() => {
    setSelectedValues(defaultVal);
  }, [defaultVal]);

  useEffect(() => {
    setHighlightedIndex(-1);
  }, [searchTerm]);

  const isSelected = useCallback(
    (option) => selectedValues.some((val) => selectionCheck(option, val)),
    [selectedValues, selectionCheck]
  );

  const handleSelect = useCallback((option) => {
    if (isDisabled) return;

    const alreadySelected = isSelected(option);
    const newSelection = alreadySelected
      ? selectedValues.filter((val) => !selectionCheck(option, val))
      : [...selectedValues, option];

    setSelectedValues(newSelection);
    onChangeCall(newSelection);
  }, [isDisabled, isSelected, selectedValues, selectionCheck, onChangeCall]);

  const handleClear = (e) => {
    e.stopPropagation();
    if (isDisabled) return;

    setSelectedValues([]);
    onChangeCall([]);
    if (onClearCall) onClearCall();
    if (!open) onBlurCallRef.current?.();
  };

  const uniqueOptions = useMemo(() => typeof allowDuplicates !== "undefined" && allowDuplicates === true ? options : erase_duplicates(options), [options, allowDuplicates]);

  const handleSelectAll = useCallback(() => {
    if (isDisabled) return;

    const allSel = uniqueOptions.every((opt) => isSelected(opt));

    if (allSel) {
      setSelectedValues([]);
      onChangeCall([]);
    } else {
      setSelectedValues([...uniqueOptions]);
      onChangeCall([...uniqueOptions]);
    }
  }, [isDisabled, uniqueOptions, isSelected, onChangeCall]);


  const compareIp = (a, b) => {
    const matchA = a.match(IP_REGEX);
    const matchB = b.match(IP_REGEX);

    if (!matchA || !matchB) return null;

    for (let i = 1; i <= 4; i++) {
      const diff = parseInt(matchA[i], 10) - parseInt(matchB[i], 10);
      if (diff !== 0) return diff;
    }

    const prefixA = matchA[5] !== undefined ? parseInt(matchA[5], 10) : 32;
    const prefixB = matchB[5] !== undefined ? parseInt(matchB[5], 10) : 32;
    return prefixA - prefixB;
  };

  const sortedOptions = useMemo(() => {
    return [...uniqueOptions].sort((a, b) => {
      const labelA = String(a.label).trim();
      const labelB = String(b.label).trim();

      const ipResult = compareIp(labelA, labelB);
      if (ipResult !== null) return ipResult;

      const isANumeric = !isNaN(labelA);
      const isBNumeric = !isNaN(labelB);

      if (isANumeric && isBNumeric) return Number(labelA) - Number(labelB);
      if (!isANumeric && !isBNumeric) return labelA.localeCompare(labelB);
      return isANumeric ? -1 : 1;
    });
  }, [uniqueOptions]);

  const filteredOptions = useMemo(() => {
    if (!showSearch) return sortedOptions;

    const searchValue = searchTerm.trim().toLowerCase();
    if (!searchValue) return sortedOptions;

    return sortedOptions.filter((option) => {
      const optionLabel = String(option?.label ?? "").toLowerCase();
      return optionLabel.includes(searchValue);
    });
  }, [sortedOptions, searchTerm, showSearch]);

  const handleSelectAllFiltered = useCallback(() => {
    if (isDisabled) return;

    const allFilteredSel = filteredOptions.every((opt) => isSelected(opt));

    if (allFilteredSel) {
      const newSelection = selectedValues.filter(
        (val) => !filteredOptions.some((opt) => selectionCheck(opt, val))
      );
      setSelectedValues(newSelection);
      onChangeCall(newSelection);
    } else {
      const optionsToAdd = filteredOptions.filter((opt) => !isSelected(opt));
      const newSelection = [...selectedValues, ...optionsToAdd];
      setSelectedValues(newSelection);
      onChangeCall(newSelection);
    }
  }, [isDisabled, filteredOptions, isSelected, selectedValues, selectionCheck, onChangeCall]);

  const isSearchActive = showSearch && searchTerm.trim().length > 0;

  // Global select-all is hidden in filter context; "Select All Results" is always
  // shown when a search is active so users can bulk-select filtered items in any context
  const showSelectAll = (usedFor !== "filter") || isSearchActive;

  const allSelected = useMemo(() => {
    if (uniqueOptions.length === 0) return false;
    return uniqueOptions.every((opt) => isSelected(opt));
  }, [uniqueOptions, isSelected]);

  const someSelected = useMemo(() => {
    const selectedCount = uniqueOptions.filter((opt) => isSelected(opt)).length;
    return selectedCount > 0 && selectedCount < uniqueOptions.length;
  }, [uniqueOptions, isSelected]);

  const allFilteredSelected = useMemo(() => {
    if (filteredOptions.length === 0) return false;
    return filteredOptions.every((opt) => isSelected(opt));
  }, [filteredOptions, isSelected]);

  const someFilteredSelected = useMemo(() => {
    const selectedCount = filteredOptions.filter((opt) => isSelected(opt)).length;
    return selectedCount > 0 && !allFilteredSelected;
  }, [filteredOptions, isSelected, allFilteredSelected]);

  const getTotalOptionsCount = useCallback(() => {
    return filteredOptions.length + (showSelectAll ? 1 : 0);
  }, [filteredOptions.length, showSelectAll]);

  const scrollHighlightedIntoView = useCallback(
    (index) => {
      const key =
        index === 0 && showSelectAll
          ? "select-all"
          : `option-${index - (showSelectAll ? 1 : 0)}`;
      const element = optionRefs.current[key];
      if (element) element.scrollIntoView({ block: "nearest", behavior: "smooth" });
    },
    [showSelectAll]
  );

  const handleListKeyDown = useCallback(
    (e) => {
      if (!open) return;

      const totalCount = getTotalOptionsCount();
      if (totalCount === 0) return;

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          e.stopPropagation();
          setHighlightedIndex((prev) => {
            const next = prev < totalCount - 1 ? prev + 1 : 0;
            setTimeout(() => scrollHighlightedIntoView(next), 0);
            return next;
          });
          break;

        case "ArrowUp":
          e.preventDefault();
          e.stopPropagation();
          setHighlightedIndex((prev) => {
            const next = prev > 0 ? prev - 1 : totalCount - 1;
            setTimeout(() => scrollHighlightedIntoView(next), 0);
            return next;
          });
          break;

        case "Enter":
        case " ":
          if (e.key === " " && e.target.tagName === "INPUT") return;
          e.preventDefault();
          e.stopPropagation();

          if (highlightedIndex < 0) return;

          if (highlightedIndex === 0 && showSelectAll) {
            if (isSearchActive) {
              handleSelectAllFiltered();
            } else {
              handleSelectAll();
            }
          } else {
            const optionIndex = showSelectAll ? highlightedIndex - 1 : highlightedIndex;
            if (filteredOptions[optionIndex]) handleSelect(filteredOptions[optionIndex]);
          }
          break;

        case "Escape":
          e.preventDefault();
          e.stopPropagation();
          close();
          break;

        default:
          break;
      }
    },
    [
      open,
      getTotalOptionsCount,
      highlightedIndex,
      showSelectAll,
      isSearchActive,
      filteredOptions,
      close,
      scrollHighlightedIntoView,
      handleSelect,
      handleSelectAll,
      handleSelectAllFiltered,
    ]
  );

  const dropdownWidth = useMemo(() => {
    const anchorWidth = anchorRef.current?.offsetWidth ?? 0;

    let maxLabelLength = 0;
    sortedOptions.forEach((opt) => {
      const labelLength = String(opt?.label ?? "").length;
      if (labelLength > maxLabelLength) maxLabelLength = labelLength;
    });

    const clampedLength = Math.min(maxLabelLength, MAX_LABEL_CHARS);
    const contentWidth = clampedLength * CHAR_WIDTH_PX + DROPDOWN_PADDING_PX;

    return Math.max(anchorWidth, contentWidth);
  }, [sortedOptions, open]);

  const renderDisplayContent = () => {
    // const numSelected = selectedValues.length;
    const numSelected = typeof allowDuplicates !== "undefined" && allowDuplicates === true
      ? uniqueOptions.filter((option) => isSelected(option)).length
      : selectedValues.length;
    const textColor = isDisabled ? "#62626E" : "#AF3241";

    if (typeof showSelectedValues !== "undefined" && showSelectedValues === true) {
      if (numSelected === 0) {
        return isFilter ? (
          <Typography variant="body2" sx={{ color: textColor }}>
            All Results
          </Typography>
        ) : null;
      }
      return (
        <Typography variant="body2" sx={{ color: textColor }}>
          {selectedValues.map((val) => val.label).join(", ")}
        </Typography>
      );
    }

    if (numSelected === 0) {
      return isFilter ? (
        <Typography variant="body2" sx={{ color: textColor }}>
          All Results
        </Typography>
      ) : null;
    }

    if (allSelected) {
      return (
        <Typography variant="body2" sx={{ color: textColor }}>
          All
        </Typography>
      );
    }

    return (
      <Typography variant="body2" sx={{ color: textColor }}>
        {numSelected + " Selected"}
      </Typography>
    );
  };

  const maxWidthStyle = enableFullWidth
    ? { maxWidth: "100%" }
    : { maxWidth: "220px", width: "100%" };

  return (
    <ClickAwayListener
      mouseEvent="onMouseDown"
      onClickAway={() => {
        if (open) close(false);
      }}
    >
      <Box sx={{ width: "100%", position: "relative", ...maxWidthStyle }}>
        <Box
          id={elemId}
          ref={anchorRef}
          role="combobox"
          aria-expanded={open}
          aria-haspopup="listbox"
          tabIndex={isDisabled ? -1 : 0}
          onClick={() => {
            if (!isDisabled) setOpen((prev) => !prev);
          }}
          onFocus={() => {
            if (!isDisabled) setIsFocused(true);
          }}
          onBlur={() => setIsFocused(false)}
          onKeyDown={(e) => {
            if (isDisabled) return;
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setOpen(true);
            }
            if (e.key === "Escape") close();
            if (e.key === "ArrowDown" || e.key === "ArrowUp") {
              e.preventDefault();
              if (!open) setOpen(true);
            }
          }}
          sx={{
            position: "relative",
            cursor: isDisabled ? "default" : "pointer",
            outline: "none",
            pt: 1.9,
            borderBottom: "1px solid #AF3241",
            opacity: isDisabled ? 0.6 : 1,
            "& .cap-clear-btn": { opacity: 0 },
            "&:hover .cap-clear-btn": { opacity: 1 },
            "& .cap-clear-btn-visible": { opacity: 1 },
            "&::after": {
              content: '""',
              position: "absolute",
              left: 0,
              bottom: -1,
              width: open ? "100%" : "0%",
              height: "2px",
              backgroundColor: "#AF3241",
              transition: "none",
            },
          }}
        >
          <Box
            sx={{
              position: "absolute",
              left: 0,
              top: shrink ? 0 : 22,
              fontSize: shrink ? 12 : 14,
              color: "#AF3241",
              pointerEvents: "none",
              transformOrigin: "top left",
              transition: "all 160ms ease",
            }}
          >
            {label}
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1, minHeight: 32 }}>
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                display: "flex",
                flexWrap: "nowrap",
                overflowX: "auto",
                overflowY: "hidden",
                gap: 0.5,
                py: 0.5,
                whiteSpace: "nowrap",
                "&::-webkit-scrollbar": { height: 6 },
              }}
            >
              {renderDisplayContent()}
            </Box>

            <Box sx={{ flex: "0 0 auto", display: "flex", alignItems: "center", gap: 0.4 }}>
              {!canNotClear && selectedValues.length > 0 && !isDisabled && (
                <Close
                  className={`cap-clear-btn${open || isFocused ? " cap-clear-btn-visible" : ""}`}
                  onClick={handleClear}
                  sx={{ cursor: "pointer", fontSize: 18 }}
                />
              )}
              {/* ------- additional icon (can be used for exceptional requirements or anything fully customizable from the calling components) ------- */}
              {
                additionalIconWithTooltip === true &&
                <Box
                  component="span"
                  sx={{ display: "inline-flex", alignItems: "center" }}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                  onKeyDown={(e) => {
                    e.stopPropagation();
                  }}
                >
                  {additionalIconMarkup(setOpen)}
                </Box>
              }
              {/* ----------------------- */}
              {open ? (
                <ExpandLessIcon fontSize="small" />
              ) : (
                <ExpandMoreIcon fontSize="small" />
              )}
            </Box>
          </Box>
        </Box>

        {fieldError && fieldError !== "" && (
          <Typography
            variant="caption"
            sx={{ color: "#AF3241", mt: 0.5, display: "block" }}
          >
            {fieldError}
          </Typography>
        )}

        <Popper
          open={open}
          anchorEl={anchorRef.current}
          popperRef={popperRef}
          placement={actualPlacement}
          popperOptions={{ strategy: anchorIsFixed ? "fixed" : "absolute" }}
          sx={{ zIndex: 1400 }}
          modifiers={[
            {
              name: "eventListeners",
              options: { scroll: false, resize: false },
            },
            {
              // Placement and height are managed by recalculate(); the dropdown
              // scrolls naturally with its anchor rather than being pinned to the viewport
              name: "preventOverflow",
              enabled: false,
            },
            {
              // Flip is managed by recalculate(); disable Popper's built-in modifier
              name: "flip",
              enabled: false,
            },
          ]}
          style={{
            minWidth: anchorRef.current?.offsetWidth ?? undefined,
            width: dropdownWidth,
            maxWidth: MAX_LABEL_CHARS * CHAR_WIDTH_PX + DROPDOWN_PADDING_PX,
          }}
        >
          <Paper
            ref={dropdownRef}
            role="listbox"
            tabIndex={-1}
            sx={{
              maxHeight: dropdownMaxHeight,
              overflowY: "auto",
              overflowX: "hidden",
              outline: "none",
            }}
            onMouseDown={(e) => {
              if (e.target.tagName !== "INPUT") e.preventDefault();
              e.stopPropagation();
            }}
            onKeyDown={handleListKeyDown}
          >
            {showSearch && (
              <Box sx={{ p: 1, pt: 0.3 }} onClick={(e) => e.stopPropagation()}>
                <CustomTextInput
                  inputRef={searchInputRef}
                  id={elemId + "_search"}
                  size="small"
                  fullWidth
                  autoComplete="off"
                  spellCheck={false}
                  label="Search"
                  value={searchTerm}
                  variant="standard"
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={handleListKeyDown}
                  InputProps={{
                    endAdornment: searchTerm ? (
                      <InputAdornment position="end">
                        <Close
                          onMouseDown={(e) => e.stopPropagation()}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSearchTerm("");
                          }}
                          sx={{ cursor: "pointer", color: "#AF3241" }}
                        />
                      </InputAdornment>
                    ) : undefined,
                  }}
                />
              </Box>
            )}

            {showSelectAll && (
              <StyledMenuItemContainer
                ref={(el) => {
                  optionRefs.current["select-all"] = el;
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  if (isSearchActive) {
                    handleSelectAllFiltered();
                  } else {
                    handleSelectAll();
                  }
                }}
                onMouseDown={(e) => e.stopPropagation()}
                onMouseEnter={() => setHighlightedIndex(0)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  marginRight: 0,
                  backgroundColor:
                    highlightedIndex === 0
                      ? "rgba(175, 50, 65, 0.08)"
                      : "transparent",
                }}
              >
                <StyledCheckbox
                  checked={isSearchActive ? allFilteredSelected : allSelected}
                  indeterminate={isSearchActive ? someFilteredSelected : someSelected}
                  onMouseDown={(e) => e.stopPropagation()}
                />
                <StyledMenuListItemText
                  primary={isSearchActive ? "Select All Results" : "Select All"}
                />
              </StyledMenuItemContainer>
            )}

            {filteredOptions.map((option, index) => {
              const optionKey = typeof allowDuplicates !== "undefined" && allowDuplicates === true
                ? `${option?.label}-${index}`
                : option?.label ?? index;
              const checked = isSelected(option);
              const listIndex = showSelectAll ? index + 1 : index;
              const isHighlighted = highlightedIndex === listIndex;

              return (
                <StyledMenuItemContainer
                  key={optionKey}
                  ref={(el) => {
                    optionRefs.current[`option-${index}`] = el;
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSelect(option);
                  }}
                  onMouseDown={(e) => e.stopPropagation()}
                  onMouseEnter={() => setHighlightedIndex(listIndex)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    marginRight: 0,
                    backgroundColor: isHighlighted
                      ? "rgba(175, 50, 65, 0.08)"
                      : "transparent",
                  }}
                >
                  <StyledCheckbox
                    checked={checked}
                    onMouseDown={(e) => e.stopPropagation()}
                  />
                  <Typography
                    variant="body2"
                    className="selectorOptions"
                    sx={{
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                      overflowWrap: "break-word",
                    }}
                  >
                    {option.label}
                  </Typography>
                </StyledMenuItemContainer>
              );
            })}

            {filteredOptions.length === 0 && (
              <Box sx={{ p: 2, textAlign: "center" }}>
                <Typography variant="body2" color="textSecondary">
                  No options found
                </Typography>
              </Box>
            )}
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
}
